import { z } from 'zod';
import { LIMITS, ROOM_OPEN_OFFSETS_MIN } from './constants.js';
import { emailSchema, screenNameSchema } from './auth.js';

/** One invited kid in the room-creation flow: screen name + parent email. */
export const roomInviteeSchema = z.object({
  screenName: screenNameSchema,
  /** Parent email — stored for later (email feature not wired yet). */
  parentEmail: emailSchema.optional(),
});
export type RoomInvitee = z.infer<typeof roomInviteeSchema>;

export const roomPolicySchema = z.object({
  allowLinks: z.boolean(),
  allowImages: z.boolean(),
  allowEmoji: z.boolean(),
});

/**
 * Room creation. Either schedule it to open `openInMinutes` from now
 * (0/5/15/30) for `durationMinutes`, OR mark it `permanent` (always open).
 */
export const createRoomSchema = z
  .object({
    name: z.string().trim().min(1).max(LIMITS.MAX_ROOM_NAME_LEN),
    /** Host parent's email (the creator). Stored; email not yet sent. */
    hostEmail: emailSchema.optional(),
    invitees: z.array(roomInviteeSchema).max(LIMITS.MAX_KIDS_PER_ROOM),
    permanent: z.boolean().default(false),
    openInMinutes: z
      .number()
      .int()
      .refine((m) => (ROOM_OPEN_OFFSETS_MIN as readonly number[]).includes(m), {
        message: 'Open the room now, or in 5, 15, or 30 minutes.',
      })
      .optional(),
    durationMinutes: z.number().int().min(5).max(720).optional(),
    policy: roomPolicySchema.optional(),
  })
  .refine((v) => v.permanent || v.openInMinutes !== undefined, {
    message: 'Pick when the room opens, or make it always-open.',
    path: ['openInMinutes'],
  })
  .refine((v) => v.permanent || v.durationMinutes !== undefined, {
    message: 'Set how long the room stays open.',
    path: ['durationMinutes'],
  });
export type CreateRoomInput = z.infer<typeof createRoomSchema>;

/** Public view of a room handed to clients (no secrets). */
export const roomViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(['scheduled', 'open', 'closed']),
  permanent: z.boolean(),
  opensAt: z.string().nullable(),
  closesAt: z.string().nullable(),
  policy: roomPolicySchema,
  /** Archived rooms are tucked away (kept for the transcript) and don't open. */
  archived: z.boolean(),
});
export type RoomView = z.infer<typeof roomViewSchema>;

/**
 * Reschedule (reopen / extend) an existing room. Same scheduling shape as
 * creation: either schedule a fresh window (`openInMinutes` from now for
 * `durationMinutes`) or make it `permanent`. Reopening also un-archives the
 * room. The server re-validates and only the host / managing adults may do it.
 */
export const rescheduleRoomSchema = z
  .object({
    permanent: z.boolean().default(false),
    openInMinutes: z
      .number()
      .int()
      .refine((m) => (ROOM_OPEN_OFFSETS_MIN as readonly number[]).includes(m), {
        message: 'Open the room now, or in 5, 15, or 30 minutes.',
      })
      .optional(),
    durationMinutes: z.number().int().min(5).max(720).optional(),
  })
  .refine((v) => v.permanent || v.openInMinutes !== undefined, {
    message: 'Pick when the room opens, or make it always-open.',
    path: ['openInMinutes'],
  })
  .refine((v) => v.permanent || v.durationMinutes !== undefined, {
    message: 'Set how long the room stays open.',
    path: ['durationMinutes'],
  });
export type RescheduleRoomInput = z.infer<typeof rescheduleRoomSchema>;

/** Duplicate a room (copies policy, schedule shape, and invited buddies). */
export const duplicateRoomSchema = z.object({
  /** Optional new name; defaults to the original name + " (copy)". */
  name: z.string().trim().min(1).max(LIMITS.MAX_ROOM_NAME_LEN).optional(),
});
export type DuplicateRoomInput = z.infer<typeof duplicateRoomSchema>;

/** Redeeming an invite link as a kid (after they're logged in). */
export const joinByInviteSchema = z.object({
  token: z.string().min(10).max(200),
});
export type JoinByInviteInput = z.infer<typeof joinByInviteSchema>;

/**
 * Mint a per-kid magic link for a room. The target kid must belong to a family
 * the caller manages and already be a participant of the room. The server
 * returns the link URL once; only the token hash is stored.
 */
export const createMagicLinkSchema = z.object({
  kidId: z.string().min(1).max(64),
});
export type CreateMagicLinkInput = z.infer<typeof createMagicLinkSchema>;

/**
 * Add a grown-up (an existing adult in the room's family) to a room so they can
 * chat in it. Trusted adults can only chat where they're added; co-parents the
 * same. The server validates the target really is a member of the family.
 */
export const addRoomAdultSchema = z.object({
  userId: z.string().min(1).max(64),
});
export type AddRoomAdultInput = z.infer<typeof addRoomAdultSchema>;

/** A grown-up currently allowed to chat in a room. */
export interface RoomAdultView {
  userId: string;
  displayName: string;
  /** Their role in the family (owner/co_parent/trusted_adult). */
  familyRole: 'owner' | 'co_parent' | 'trusted_adult';
  addedAt: string;
}

/** A family adult who could be added to a room (not the host, not yet added). */
export interface RoomAdultCandidate {
  userId: string;
  displayName: string;
  familyRole: 'owner' | 'co_parent' | 'trusted_adult';
}

/** A room a grown-up has been added to (their entry point to the live chat). */
export interface AddedRoomView {
  room: RoomView;
  /** The screen name / display of who hosts it, for context. */
  hostName: string | null;
}

/** A room in the caller's manageable-rooms list — flags whether they personally host it,
 *  vs. it being hosted by another adult in their family (still manageable, per canManageRoom). */
export interface ParentRoomView extends RoomView {
  hostedByYou: boolean;
}
